<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import { useCampaignDrip } from 'dashboard/composables/useCampaignDrip';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import EditScheduledCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/EditScheduledCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import CampaignProgressDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignProgressDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';

const { t } = useI18n();
const getters = useStoreGetters();
const { handleStart, handlePause, handleResume, handleCancel, handlePreview } =
  useCampaignDrip();

const selectedCampaign = ref(null);
// Modal central (DS Dialog) aberto via ref — disparado pelo botão do topo OU pelo
// CTA "criar nova" embaixo da lista.
const campaignDialogRef = ref(null);
const openCreate = () => campaignDialogRef.value?.open();
const editScheduledCampaignDialogRef = ref(null);
const handleEdit = campaign =>
  editScheduledCampaignDialogRef.value?.open(campaign);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);
const progressDialogRef = ref(null);
const handleProgress = campaign => progressDialogRef.value?.open(campaign);

const WhatsAppCampaigns = computed(
  () => getters['campaigns/getWhatsAppCampaigns'].value
);

const hasNoWhatsAppCampaigns = computed(
  () => WhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
    @click="openCreate"
  >
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <template v-else-if="!hasNoWhatsAppCampaigns">
      <CampaignList
        :campaigns="WhatsAppCampaigns"
        @delete="handleDelete"
        @edit="handleEdit"
        @start="handleStart"
        @pause="handlePause"
        @resume="handleResume"
        @cancel="handleCancel"
        @preview="handlePreview"
        @progress="handleProgress"
      />
      <!-- Criar nova campanha logo abaixo das campanhas recentes -->
      <div class="flex justify-center mt-4">
        <Button
          :label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
          icon="i-ri-add-line"
          size="sm"
          @click="openCreate"
        />
      </div>
    </template>
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <WhatsAppCampaignDialog ref="campaignDialogRef" />
    <EditScheduledCampaignDialog ref="editScheduledCampaignDialogRef" />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <CampaignProgressDialog ref="progressDialogRef" />
  </CampaignLayout>
</template>
